import React, { useMemo } from 'react';
import { Button, Grid, Slider } from '@mui/material';
import { Controller } from 'react-hook-form';
import { FilterModel } from '../../models';
import { useModelTypeOptions } from '../../hooks';
import RefreshIcon from '@mui/icons-material/Refresh';
import style from './index.module.scss';
import renderColoredIconOption from 'components/select-render-components/render-colored-icon-option';
import renderColoredIconTags from 'components/select-render-components/render-colored-icon-tags';
import { useTranslate } from 'hooks/use-translate';
import { AppSelect } from 'components/app-select';
import { addYears } from 'date-fns';
import { useFilterForm } from 'hooks/use-filter-form';
import { composeFunctions } from 'utils';
import { calcRangeSlider } from 'utils/app-helpers';
import { useIsBelowMd } from 'hooks/use-is-below-md';
import { countActiveFilters, useFiltersDialog } from 'hooks/use-filters-dialog';
import { FiltersButton, FiltersDialog } from 'components/filters-dialog';

const makeYearsSource = (startYear: number) => {
  const START = startYear;
  const endDate = new Date();

  const currentYear = addYears(endDate, 1).getFullYear();
  const length = currentYear - (START - 1);
  return new Array(length).fill(null).map((_, i) => {
    const id = currentYear - i;
    return { id, title: String(id) };
  });
};

// N1 (brief pattern B): below md the years and the types live in the filters
// dialog; the refresh stays at rest beside the filters button
const DIALOG_FIELDS = ['fromYear', 'toYear', 'types'] as const;

interface ToolsProps {
  startYear: number | null;
  filters: FilterModel;
  /** The list's initial filters: what the filters dialog's "clear" restores. */
  defaults: FilterModel;
  setFilters: (value: FilterModel) => void;
  isLoading: boolean;
  onRefresh: () => void;
}
export const Tools: React.FC<React.PropsWithChildren<ToolsProps>> = ({
  isLoading,
  filters,
  defaults,
  setFilters,
  onRefresh,
  startYear,
}) => {
  const { t } = useTranslate();

  const { control, setValue, watch, onCheckAndUpdate, getValues, reset } = useFilterForm({
    defaultValues: filters,
    onUpdate: setFilters,
  });

  const from = watch('fromYear');
  const to = watch('toYear');
  const sliderValue = [from, to];

  const marks = useMemo(() => {
    if (!startYear) return [];

    return makeYearsSource(startYear)
      .map(({ id, title }) => ({ value: id, label: title }))
      .sort((a, b) => a.value - b.value);
  }, [startYear]);

  const sliderProps = useMemo(() => {
    const values = marks.map(({ value }) => value);
    const min = Math.min(...values);
    const max = Math.max(...values);

    return {
      marks,
      min,
      max,
      onChange: (e: Event, value: number | number[]) => {
        if (!Array.isArray(value)) {
          return;
        }

        const oldValue = getValues('fromYear');
        const activeThumb = oldValue === value[0] ? 1 : 0;

        const res = calcRangeSlider({ value, marks, activeThumb, minStep: 0, maxStep: 2 });

        setValue('fromYear', res[0]);
        setValue('toYear', res[1]);
      },
    };
  }, [setValue, getValues, marks]);

  const options = useModelTypeOptions();

  // below md a field in the dialog edits the draft; "apply" commits it
  const isBelowMd = useIsBelowMd();
  const onFieldChange = isBelowMd ? undefined : onCheckAndUpdate;
  const dialog = useFiltersDialog({
    form: { reset, getValues },
    applied: filters,
    cleared: defaults,
    fields: DIALOG_FIELDS,
    onApply: onCheckAndUpdate,
  });
  // the year range is one filter: its two ends count once
  const count =
    countActiveFilters(filters, defaults, ['types']) +
    Math.min(countActiveFilters(filters, defaults, ['fromYear', 'toYear']), 1);

  const slider = (
    <Slider
      value={sliderValue}
      step={null}
      {...sliderProps}
      onChangeCommitted={onFieldChange}
      className={style.slider}
    />
  );
  const fieldTypes = (
    <Controller
      name={'types'}
      control={control}
      render={({ field: { value, onChange } }) => {
        return (
          <AppSelect
            label={t('types')}
            RenderInputProps={{
              slotProps: { inputLabel: { shrink: true } },
            }}
            multiple
            placeholder={t('all-types')}
            value={value}
            options={options}
            onChange={composeFunctions(onChange, onFieldChange)}
            renderOption={renderColoredIconOption}
            renderValue={renderColoredIconTags}
            className={style.select}
          />
        );
      }}
    />
  );
  const buttonRefresh = (
    <Button
      color={'primary'}
      variant={'contained'}
      onClick={onRefresh}
      disabled={isLoading}
      startIcon={<RefreshIcon />}
      className={style.refresh}
    >
      {t('refresh')}
    </Button>
  );

  if (isBelowMd) {
    return (
      <div className={style.rest}>
        <FiltersButton count={count} onClick={dialog.open} />
        {buttonRefresh}
        <FiltersDialog
          open={dialog.isOpen}
          onClose={dialog.close}
          onApply={dialog.apply}
          onClear={dialog.clear}
        >
          {slider}
          {fieldTypes}
        </FiltersDialog>
      </div>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <Grid size={12}>{slider}</Grid>
        <Grid
          // PD-08 / M-03 (R2). An `lg`-only size shrink-wraps the item below
          // lg (D-70 (4), the same trap in a second place): next to the
          // refresh button the select collapsed to 30 px and its placeholder
          // "הצג הכל", which needs 53 px, read as three glyphs. `xs: 12` gives
          // it the row below lg; the lg value is untouched, so desktop is too.
          // md..lg keeps its full row (below md the filters dialog renders
          // instead, N1).
          size={{
            xs: 'grow',
            md: 12,
            lg: 'grow',
          }}
        >
          {fieldTypes}
        </Grid>
        <Grid style={{ width: 'unset' }} size="auto">
          {buttonRefresh}
        </Grid>
      </Grid>
    </>
  );
};
